/**
 * MarkdownBody — render a server-sanitized markdown body.
 *
 * The server (caitapi/services/community._sanitize_markdown) runs nh3
 * against every write path, stripping <script>, <style>, on*-handler
 * attributes, non-http/https/mailto URL schemes, and any tag outside
 * the safe Tiptap subset. By the time a body reaches this component
 * it's already known-safe markdown + a small subset of HTML.
 *
 * react-markdown + remark-gfm handles GitHub-flavored markdown
 * (tables, task lists, autolinks) the same as the lessons rendering
 * stack.
 */
import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export interface MarkdownBodyProps {
  body: string;
  /** Optional class to scope typography styles. */
  className?: string;
}

export function MarkdownBody({
  body,
  className,
}: MarkdownBodyProps): React.JSX.Element {
  return (
    <div className={className ?? "prose prose-sm max-w-none"}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Force every rendered <a> to open in a new tab + carry
          // safe rel attributes. Mirrors nh3's server-side default for
          // user-supplied links.
          a: ({ children, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}
